import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'

import { observer } from 'mobx-react'

import CustomFields from 'modules/custom_resources/store/collections/customFields/fields'
import CustomValues from 'modules/custom_resources/store/collections/customFields/values'
import type CustomField from 'modules/custom_resources/store/models/customFields/Field'
import ReviewCompetencyAssessment from 'modules/reviews/components/Competencies/ReviewCompetencyAssessment'
import useCompetencyAssessments from 'modules/reviews/components/Competencies/ReviewCompetencyAssessment/useCompetencyAssessments'
import QuestionsWithCategory from 'modules/reviews/components/WriteResponse/QuestionsWithCategory'
import QuestionsWithoutCategory from 'modules/reviews/components/WriteResponse/QuestionsWithoutCategory'
import { useScore } from 'modules/reviews/hooks/useScore'
import { ScoreContext } from 'modules/reviews/lib/PerformanceScoreContext'
import type { ValueType } from 'modules/reviews/lib/coerceValueFromType'
import coerceValueFromType from 'modules/reviews/lib/coerceValueFromType'
import type QuestionGroup from 'modules/reviews/store/models/QuestionGroup'

import QuestionsWithoutCategoryWithScoreType from '../QuestionsWithoutCategoryWithScoreType'

interface Props {
  questionGroup: QuestionGroup
  assessCompetencies?: boolean
}

function ResponseValues({ questionGroup, assessCompetencies }: Props) {
  const questionGroupWithCategory = questionGroup.withCategory
  const questionsWithoutCategoryWithoutType = questionGroup.withoutCategoryWithoutType
  const questionsWithoutCategoryWithScoreType = questionGroup.withoutCategoryWithScoreType

  const { competencyAssessments } = useCompetencyAssessments(questionGroup.competenciesAssessments)

  const filterByCategory = React.useRef(
    groupBy(questionGroupWithCategory, (q: any) => q.surveys_question_category_id)
  ).current

  const uniqueCategories = uniq(
    questionGroupWithCategory.map((q: any) => q.surveys_question_category_id).sort((a, b) => a - b)
  )

  const createForm = (field: CustomField, value: ValueType) => {
    const values = {
      field_id: field.get('id'),
      field_type: field.get('field_type'),
      mandatory: field.get('required'),
      name: field.get('label'),
      min_value: field.get('min_value'),
      max_value: field.get('max_value'),
      value,
    }

    return new Form(values, {
      field_id: 'number',
      field_type: 'string',
      mandatory: 'boolean',
      name: 'string',
      min_value: 'number',
      max_value: 'number',
      value: 'string',
    })
  }

  // Questions without category with null question_type
  const customFieldsWithoutCategoryWithoutType = React.useRef(
    new CustomFields(
      questionsWithoutCategoryWithoutType.map((payload: any) => {
        if (!payload.custom_field) return payload

        return { ...payload.custom_field, position: payload.position }
      }),
      {
        model_ref: 'QuestionGroup',
        instance_id: questionGroup.attributes.id,
      }
    )
  ).current

  const customValuesWithoutCategoryWithoutType = React.useRef(
    new CustomValues(questionGroup.attributes.answers, {
      model_ref: 'QuestionGroup',
      instance_id: questionGroup.attributes.id,
    })
  ).current

  const optionsWithoutCategoryWithoutType = React.useMemo(() => {
    return customFieldsWithoutCategoryWithoutType.models.reduce((memo: any, cf) => {
      if (!cf.get('choice_options')) return memo

      memo[cf.get('id')] = cf.get('choice_options')

      return memo
    }, {})
  }, [customFieldsWithoutCategoryWithoutType])

  const formsWithoutCategoryWithoutType = React.useRef(
    sortBy(customFieldsWithoutCategoryWithoutType.models, (cf) => cf.get('position')).map(
      (field) => {
        const answer = customValuesWithoutCategoryWithoutType.find({
          field_id: field.get('id'),
        })

        const value = answer ? coerceValueFromType(field, answer.attributes.value) : ''

        return createForm(field, value)
      }
    )
  ).current

  // Questions With category
  const customFieldsWithCategory = React.useRef(
    new CustomFields(
      questionGroupWithCategory.map((payload: any) => {
        if (!payload.custom_field) return payload

        return { ...payload.custom_field, position: payload.position }
      }),
      {
        model_ref: 'QuestionGroup',
        instance_id: questionGroup.attributes.id,
      }
    )
  ).current

  const customValuesWithCategory = React.useRef(
    new CustomValues(questionGroup.attributes.answers, {
      model_ref: 'QuestionGroup',
      instance_id: questionGroup.attributes.id,
    })
  ).current

  const optionsWithCategory = React.useMemo(() => {
    return customFieldsWithCategory.models.reduce((memo: any, cf) => {
      if (!cf.get('choice_options')) return memo

      memo[cf.get('id')] = cf.get('choice_options')

      return memo
    }, {})
  }, [customFieldsWithCategory])

  const formsWithCategory = React.useRef<any>(
    sortBy(customFieldsWithCategory.models, (cf) => cf.get('position')).map((field) => {
      const answer = customValuesWithCategory.find({
        field_id: field.get('id'),
      })

      const value = answer ? coerceValueFromType(field, answer.attributes.value) : ''

      return createForm(field, value)
    })
  ).current

  // Questions without category with score question_type
  const customFieldsWithoutCategoryWithScoreType = React.useRef(
    new CustomFields(
      questionsWithoutCategoryWithScoreType.map((payload: any) => {
        if (!payload.custom_field) return payload

        return { ...payload.custom_field, position: payload.position }
      }),
      {
        model_ref: 'surveys-question_group',
        instance_id: questionGroup.attributes.id,
      }
    )
  ).current

  const customValuesWithoutCategoryWithScoreType = React.useRef(
    new CustomValues(questionGroup.attributes.answers, {
      model_ref: 'QuestionGroup',
      instance_id: questionGroup.attributes.id,
    })
  ).current

  const optionsWithoutCategoryWithScoreType = React.useMemo(() => {
    return customFieldsWithoutCategoryWithScoreType.models.reduce<Record<string, any>>(
      (memo, cf) => {
        memo[cf.attributes.id] = cf.choiceOptions.filter((opt) => opt.isActive)

        return memo
      },
      {}
    )
  }, [customFieldsWithoutCategoryWithScoreType])

  const formsWithoutCategoryWithScoreType = React.useRef(
    sortBy(customFieldsWithoutCategoryWithScoreType.models, (cf) => cf.attributes.position).map(
      (field) => {
        const answer = customValuesWithoutCategoryWithScoreType.find({
          field_id: field.attributes.id,
        })

        const value = answer ? coerceValueFromType(field, answer.attributes.value) : ''

        return createForm(field, value)
      }
    )
  ).current

  const { calculateScore } = useScore()
  const { setScore } = React.useContext(ScoreContext)
  setScore(calculateScore(formsWithCategory.concat(formsWithoutCategoryWithoutType)))

  return (
    <>
      <Box gap="s48" padding="s24">
        {formsWithoutCategoryWithoutType.map((form, index) => {
          const field = customFieldsWithoutCategoryWithoutType.get(
            Number(form.get('field_id').value)
          )

          if (!field) {
            return null
          }

          return (
            <QuestionsWithoutCategory
              key={index}
              form={form}
              options={optionsWithoutCategoryWithoutType}
              field={field}
              isResponseValues
            />
          )
        })}
      </Box>
      {uniqueCategories &&
        uniqueCategories.map((categoryId) => {
          const firstFilter: any = filterByCategory[categoryId]?.[0]

          return (
            <QuestionsWithCategory
              key={firstFilter.surveys_question_category.name}
              categoryName={firstFilter.surveys_question_category.name}
              questionCategories={filterByCategory[categoryId] ?? []}
              formsWithCategory={formsWithCategory}
              optionsWithCategory={optionsWithCategory}
              isResponseValues
            />
          )
        })}
      {assessCompetencies && (
        <ReviewCompetencyAssessment
          assessments={competencyAssessments}
          readonly
          accessCompetencies={assessCompetencies}
        />
      )}
      {!!formsWithoutCategoryWithScoreType.length && (
        <QuestionsWithoutCategoryWithScoreType
          formsWithoutCategoryWithScoreType={formsWithoutCategoryWithScoreType}
          customFieldsWithoutCategoryWithScoreType={customFieldsWithoutCategoryWithScoreType}
          optionsWithoutCategoryWithScoreType={optionsWithoutCategoryWithScoreType}
          isResponseValues
        />
      )}
    </>
  )
}

export default observer(ResponseValues)
